import { TemplateService } from './templateService';
import { configService } from './configService';
import { WordService } from './wordService';
import { DocumentMetadataService } from '../../features/knowledgebase/services/documentMetadataService';

export interface SaveState {
  isSaving: boolean;
  lastSavedAt: Date | null;
  error: string | null;
}

export type SaveConflictChoice = 'reload' | 'overwrite' | 'cancel';

export type SaveConflictVariant = 'remote-newer' | 'missing-baseline';

/** Thrown when `throwOnConflictUserChoice` is set and the user closes the conflict UI via Cancel or Load latest. */
export class SaveConflictUserResolutionError extends Error {
  readonly resolution: 'cancel' | 'reload';

  constructor(resolution: 'cancel' | 'reload') {
    super(
      resolution === 'cancel'
        ? 'Save cancelled after cloud conflict prompt'
        : 'Document reloaded from cloud; save not uploaded'
    );
    this.name = 'SaveConflictUserResolutionError';
    this.resolution = resolution;
  }
}

export interface SaveConflictInfo {
  lastKnownFileName: string;
  remoteFileName: string;
  variant: SaveConflictVariant;
}

export interface SaveDocumentOptions {
  /**
   * When false (e.g. periodic autosave), a remote-newer conflict skips save and notifies; no UI.
   * @default true
   */
  allowConflictDialog?: boolean;
  /**
   * When true: cloud has a file but this session has no baseline head — still prompt (manual / pre-switch saves).
   * When false explicitly: skip missing-baseline prompt (pre-send save, etc.).
   * When omitted: follows `allowConflictDialog` (interactive save ⇒ full check; background save ⇒ no baseline prompt).
   */
  promptIfNoBaseline?: boolean;
  /**
   * When true, Cancel or Load latest in the conflict dialog throws {@link SaveConflictUserResolutionError}
   * so callers can abort (e.g. do not change execution after a non-upload resolution).
   */
  throwOnConflictUserChoice?: boolean;
}

export class StorageService {
  private static autoSaveInterval: NodeJS.Timeout | null = null;
  private static lastSavedHash: string | null = null;
  private static isSaving: boolean = false;
  private static lastSavedAt: Date | null = null;

  /**
   * In-memory: latest .docx file name we believe matches Word for each workpill_execution_templates.id folder.
   * Not stored inside the .docx (avoids stale embedded revision on upload).
   */
  private static executionTemplateHeadById: Map<string, string> = new Map();

  private static saveConflictHandler: ((info: SaveConflictInfo) => Promise<SaveConflictChoice>) | null =
    null;

  private static listeners: Array<(state: SaveState) => void> = [];

  /**
   * Call after loading a template from Storage into Word so save conflict detection has a baseline.
   */
  static noteLoadedExecutionTemplateHead(executionTemplateId: string, fileName: string): void {
    this.executionTemplateHeadById.set(executionTemplateId, fileName);
  }

  /**
   * Register UI handler for save conflicts (e.g. from MainApplication). Clears on unmount.
   */
  static setSaveConflictHandler(handler: ((info: SaveConflictInfo) => Promise<SaveConflictChoice>) | null): void {
    this.saveConflictHandler = handler;
  }

  /**
   * Refresh autosave text-hash after loading a document from storage into Word.
   */
  static async syncAutosaveBaselineFromWord(): Promise<void> {
    try {
      const currentContent = await WordService.getFullDocumentText();
      this.lastSavedHash = await this.hashString(currentContent);
    } catch (e) {
      console.warn('[StorageService] syncAutosaveBaselineFromWord failed:', e);
    }
  }

  private static isRemoteHeadNewerThanLastKnown(remoteFileName: string, lastKnownFileName: string): boolean {
    return remoteFileName > lastKnownFileName;
  }

  /**
   * Replace Word document with latest blob in execution-templates and sync head + autosave baseline.
   */
  static async reloadLatestExecutionTemplateInWord(executionTemplateId: string): Promise<void> {
    const downloaded = await TemplateService.downloadLatestTemplate(executionTemplateId);
    if (!downloaded) {
      throw new Error('No document found in cloud for this template');
    }
    await WordService.loadDocumentFromBlob(downloaded.blob);
    this.noteLoadedExecutionTemplateHead(executionTemplateId, downloaded.fileName);
    await this.syncAutosaveBaselineFromWord();
  }

  /**
   * Suscribirse a cambios de estado de guardado
   */
  static subscribe(callback: (state: SaveState) => void): () => void {
    this.listeners.push(callback);
    // Notificar estado actual inmediatamente
    callback({
      isSaving: this.isSaving,
      lastSavedAt: this.lastSavedAt,
      error: null
    });
    
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  private static notifyListeners(error: string | null = null): void {
    const state: SaveState = {
      isSaving: this.isSaving,
      lastSavedAt: this.lastSavedAt,
      error
    };
    this.listeners.forEach(listener => listener(state));
  }

  /**
   * Guarda el documento actual usando TemplateService
   */
  static async saveDocumentToSupabase(options?: SaveDocumentOptions): Promise<string | null> {
    const allowConflictDialog = options?.allowConflictDialog !== false;
    const promptIfNoBaseline =
      options?.promptIfNoBaseline !== undefined
        ? options.promptIfNoBaseline === true
        : allowConflictDialog;
    const throwOnConflictUserChoice = options?.throwOnConflictUserChoice === true;

    if (this.isSaving) {
      console.log('⚠️ Ya hay un guardado en progreso, saltando...');
      return null;
    }

    try {
      this.isSaving = true;
      this.notifyListeners();
      console.log('💾 Guardando documento en Supabase...');

      const config = configService.getConfig();

      if (!config.executionId) {
        console.warn('⚠️ No se puede guardar: No hay ejecución seleccionada');
        this.notifyListeners('No hay ejecución seleccionada');
        return null;
      }

      let executionTemplateId = config.activeExecutionTemplateId;
      if (!executionTemplateId) {
        executionTemplateId = await TemplateService.getPrimaryDocxExecutionTemplateId(
          config.executionId
        );
        if (executionTemplateId) {
          configService.setActiveExecutionTemplateId(executionTemplateId);
        }
      }
      if (!executionTemplateId) {
        console.warn('⚠️ No se puede guardar: No hay plantilla Word en la ejecución');
        this.notifyListeners('No hay plantilla Word en la ejecución');
        return null;
      }

      const remoteHead = await TemplateService.getLatestTemplateFileName(executionTemplateId);
      const lastHead = this.executionTemplateHeadById.get(executionTemplateId) ?? null;

      const missingBaseline = promptIfNoBaseline && !lastHead && !!remoteHead;
      const remoteNewer =
        !!lastHead && !!remoteHead && this.isRemoteHeadNewerThanLastKnown(remoteHead, lastHead);
      const needsUserConflictDecision = !!remoteHead && (remoteNewer || missingBaseline);

      if (needsUserConflictDecision) {
        const msg = missingBaseline
          ? 'A document exists in the cloud but this session has no sync baseline. Use the conflict dialog or reload the template.'
          : 'A newer document exists in the cloud. Reload the latest version or use Upload anyway in the conflict dialog.';
        if (!allowConflictDialog || !this.saveConflictHandler) {
          console.warn(
            '[StorageService] Save skipped:',
            missingBaseline ? 'no baseline' : 'remote newer',
            lastHead,
            '->',
            remoteHead
          );
          this.notifyListeners(msg);
          return null;
        }
        const choice = await this.saveConflictHandler({
          lastKnownFileName: lastHead ?? '—',
          remoteFileName: remoteHead,
          variant: remoteNewer ? 'remote-newer' : 'missing-baseline',
        });
        if (choice === 'cancel') {
          if (throwOnConflictUserChoice) {
            throw new SaveConflictUserResolutionError('cancel');
          }
          return null;
        }
        if (choice === 'reload') {
          await this.reloadLatestExecutionTemplateInWord(executionTemplateId);
          if (throwOnConflictUserChoice) {
            throw new SaveConflictUserResolutionError('reload');
          }
          return null;
        }
      }

      const metaForSave = await DocumentMetadataService.readMetadata();
      console.log('📝 Guardando metadata en el documento...');
      await DocumentMetadataService.saveMetadata({
        ...metaForSave,
        executionId: config.executionId,
        workpillId: config.workpillId,
        userId: config.userId,
      });

      const result = await TemplateService.saveCurrentDocumentTemplate(executionTemplateId);

      if (result.success && result.filePath) {
        const newHead = TemplateService.fileNameFromStoragePath(result.filePath);
        this.executionTemplateHeadById.set(executionTemplateId, newHead);

        console.log('✅ Documento guardado exitosamente:', result.filePath);
        this.lastSavedAt = new Date();

        await TemplateService.cleanupOldTemplates(executionTemplateId, 10);

        this.notifyListeners();
        return result.filePath;
      } else {
        console.error('❌ Error guardando:', result.error);
        this.notifyListeners(result.error || 'Error al guardar');
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('💥 Error en saveDocumentToSupabase:', error);
      this.notifyListeners(error instanceof Error ? error.message : 'Error desconocido');
      throw error;
    } finally {
      this.isSaving = false;
      this.notifyListeners();
    }
  }

  /**
   * Inicia auto-guardado periódico (cada X minutos)
   */
  static startAutoSave(intervalMinutes: number = 5): void {
    if (this.autoSaveInterval) {
      console.log('⚠️ Auto-guardado ya está activo');
      return;
    }

    console.log(`🔄 Iniciando auto-guardado cada ${intervalMinutes} minutos`);
    
    this.autoSaveInterval = setInterval(async () => {
      try {
        // Verificar si el documento cambió
        const currentContent = await WordService.getFullDocumentText();
        const currentHash = await this.hashString(currentContent);
        
        if (currentHash !== this.lastSavedHash) {
          console.log('📝 Documento modificado, guardando...');
          const path = await this.saveDocumentToSupabase({ allowConflictDialog: false });
          if (path !== null) {
            this.lastSavedHash = currentHash;
          }
        } else {
          console.log('✓ Sin cambios desde el último guardado');
        }
      } catch (error) {
        console.error('❌ Error en auto-guardado:', error);
      }
    }, intervalMinutes * 60 * 1000);
  }

  /**
   * Detiene el auto-guardado
   */
  static stopAutoSave(): void {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
      console.log('🛑 Auto-guardado detenido');
    }
  }

  /**
   * Genera un hash simple del contenido
   */
  private static async hashString(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Obtiene la URL del último documento guardado
   */
  static async getLatestDocumentUrl(): Promise<string | null> {
    const config = configService.getConfig();
    
    if (!config.executionId) {
      console.warn('⚠️ No se puede obtener URL: No hay ejecución seleccionada');
      return null;
    }

    let etId = config.activeExecutionTemplateId;
    if (!etId) {
      etId = await TemplateService.getPrimaryDocxExecutionTemplateId(config.executionId);
    }
    if (!etId) {
      console.warn('⚠️ No se puede obtener URL: sin execution template Word');
      return null;
    }

    return await TemplateService.getLatestTemplateUrl(etId);
  }

  /**
   * Guarda manualmente (para botón en UI)
   */
  static async manualSave(): Promise<{ success: boolean; path?: string; error?: string }> {
    try {
      const path = await this.saveDocumentToSupabase();
      return { success: true, path: path || undefined };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  /**
   * Carga el último template de una ejecución específica en Word
   * Si no existe execution-template, intenta cargar el template original del workpill
   * @param workpillId ID del workpill
   * @param executionId ID de la ejecución
   */
  static async loadTemplateForExecution(
    workpillId: string, 
    executionId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔄 ========================================');
      console.log('🔄 === CAMBIANDO A NUEVA EJECUCIÓN ===');
      console.log('🔄 ========================================');
      console.log('🔄 WorkpillId:', workpillId);
      console.log('🔄 ExecutionId:', executionId);

      const executionTemplateId =
        await TemplateService.getPrimaryDocxExecutionTemplateId(executionId);
      if (executionTemplateId) {
        configService.setActiveExecutionTemplateId(executionTemplateId);
      }

      let loadedFileName: string | null = null;
      const downloaded = executionTemplateId
        ? await TemplateService.downloadLatestTemplate(executionTemplateId)
        : null;
      let blob: Blob | null = downloaded?.blob ?? null;
      if (downloaded) {
        loadedFileName = downloaded.fileName;
      }

      if (!blob) {
        console.log('⚠️ No hay execution-template guardado para esta ejecución');
        console.log('🔍 Buscando template original del workpill...');
        
        // 2. Si no existe, intentar cargar el template original del workpill
        const originalBlob = await TemplateService.downloadOriginalWorkpillTemplate(workpillId);
        
        if (!originalBlob) {
          console.log('⚠️ Tampoco hay template original del workpill');
          console.log('ℹ️ Se mantiene el documento actual en Word');
          return { 
            success: true // No es un error, simplemente no hay template
          };
        }

        console.log('✅ Template original encontrado');
        if (!executionTemplateId) {
          console.log('ℹ️ Sin fila workpill_execution_templates (word); no se persiste en execution-templates');
          blob = originalBlob;
        } else {
          console.log('💾 Guardando template original como primera execution-template...');
          const saveResult = await TemplateService.saveBlobAsExecutionTemplate(
            originalBlob,
            executionTemplateId
          );

          if (!saveResult.success) {
            console.warn('⚠️ No se pudo guardar el template original como execution-template');
            console.warn('   Continuando de todas formas con la carga en Word...');
            const fallbackName = await TemplateService.getLatestTemplateFileName(executionTemplateId);
            if (fallbackName) {
              loadedFileName = fallbackName;
            }
          } else {
            console.log('✅ Template original guardado como primera versión');
            if (saveResult.filePath) {
              loadedFileName = TemplateService.fileNameFromStoragePath(saveResult.filePath);
            }
          }
          blob = originalBlob;
        }
      } else {
        console.log('✅ Execution-template encontrado');
      }

      // 5. Cargar el documento en Word
      console.log('📂 Cargando documento en Word...');
      await WordService.loadDocumentFromBlob(blob);

      if (executionTemplateId) {
        if (loadedFileName) {
          this.noteLoadedExecutionTemplateHead(executionTemplateId, loadedFileName);
        }
        await this.syncAutosaveBaselineFromWord();
      }
      
      console.log('✅ ========================================');
      console.log('✅ TEMPLATE CARGADO EXITOSAMENTE');
      console.log('✅ ========================================');

      return { success: true };
      
    } catch (error) {
      console.error('💥 Error cargando template:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }
}








